/** A source's header: back link, breadcrumb, editable name/kind/status/owner, sub-sources, period, overflow. */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArrowLeft, ChevronRight, MoreHorizontal, Pencil, Users } from "lucide-react";
import { Input } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Segmented, dotClass } from "@/features/command/ui";
import type { BackTarget } from "@/lib/back-target";
import { KindMenu, Menu, MenuItem, OwnerMenu, SourceGlyph, StatusMenu, StatusPill } from "../shared";
import type { DetailResponse, SourceRow, TeamMember } from "../types";

export function DetailHeader({
  data, back, days, onDays, onPatch, onArchive, team, from,
}: {
  data: DetailResponse;
  back: BackTarget;
  days: number;
  onDays: (d: number) => void;
  onPatch: (body: Partial<SourceRow>) => Promise<boolean>;
  onArchive: () => void;
  team: TeamMember[];
  from: string;
}) {
  const s = data.source;
  return (
    <div className="space-y-4">
      <Link to={back.to} className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-100">
        <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
      </Link>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <SourceGlyph icon={s.icon} kind={s.kind} color={s.color} size="lg" className="mt-0.5 hidden sm:inline-flex" />
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-300/80">
              <Link to="/traffic" className="hover:text-indigo-200">Traffic</Link>
              {s.parent_slug && (
                <>
                  <ChevronRight className="h-3 w-3 text-zinc-600" />
                  <Link to={`/traffic/${s.parent_slug}`} state={{ from }} className="truncate hover:text-indigo-200">{s.parent_name}</Link>
                </>
              )}
            </div>
            <InlineName value={s.name} onSave={(name) => onPatch({ name })} />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusMenu status={s.status} onChange={(status) => void onPatch({ status })} />
              <KindMenu kind={s.kind} onChange={(kind) => void onPatch({ kind })} />
              <OwnerMenu
                ownerId={s.owner_id}
                ownerName={s.owner_name}
                team={team}
                onChange={(owner_id, owner_name) => void onPatch({ owner_id, owner_name })}
              />
            </div>
            {s.description && <p className="mt-2 max-w-2xl text-sm text-zinc-400">{s.description}</p>}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Segmented size="sm" value={String(days)} onChange={(v) => onDays(Number(v))} options={[7, 30, 90].map((d) => ({ value: String(d), label: `${d} days` }))} />
          <Menu
            align="right"
            width={230}
            trigger={({ toggle }) => (
              <button type="button" onClick={toggle} title="More" className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-400 transition-colors hover:text-zinc-100">
                <MoreHorizontal className="h-4 w-4" />
              </button>
            )}
          >
            {(close) => (
              <>
                <Link to={`/contacts?source=${s.id}`} state={{ from }} onClick={close} className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800/70">
                  <Users className="h-4 w-4 text-zinc-400" /> See everyone in People
                </Link>
                <div className="my-1 border-t border-zinc-800" />
                <MenuItem danger icon={Archive} hint="Its people keep their credit" onClick={() => { close(); onArchive(); }}>Archive this source</MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>

      {data.children.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-500">Inside it:</span>
          {data.children.map((c) => (
            <Link
              key={c.id}
              to={`/traffic/${c.slug}${days !== 30 ? `?days=${days}` : ""}`}
              state={{ from }}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 py-1 pl-2.5 pr-1.5 text-xs text-zinc-200 transition-colors hover:border-indigo-400/40 hover:text-zinc-50"
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", dotClass(c.color))} />
              {c.name}
              <StatusPill status={c.status} className="scale-90" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function InlineName({ value, onSave }: { value: string; onSave: (v: string) => Promise<boolean> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => { if (editing) ref.current?.select(); }, [editing]);

  const commit = async () => {
    const v = draft.trim();
    setEditing(false);
    if (v && v !== value) await onSave(v);
  };

  if (editing) {
    return (
      <Input
        ref={ref}
        value={draft}
        maxLength={120}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        className="h-11 max-w-xl text-2xl font-semibold tracking-tight"
      />
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} title="Rename" className="group/nm flex max-w-full items-center gap-2 text-left">
      <h1 className="truncate text-2xl font-semibold tracking-tight text-zinc-50 sm:text-[28px]">{value}</h1>
      <Pencil className="h-4 w-4 shrink-0 text-zinc-600 opacity-0 transition-opacity group-hover/nm:opacity-100" />
    </button>
  );
}
